import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import TopBar from 'elements/TopBar/TopBar';
import { ProjectConfig } from 'Global';
import {
  getCalendarioLiturgicoLabel,
  getMartyrologyDesignationLabel,
  getSantoralLabel,
  getSistemaLabel,
} from 'hooks/useLabels';

interface MartyrologyStatus {
  available: boolean;
  browse_ready?: boolean;
  complete: boolean;
  total_entries: number;
}

interface MartyrologyEntry {
  id: string;
  month?: number | null;
  day?: number | null;
  name: string;
  designation?: string | null;
  place?: string | null;
  period?: string | null;
  position?: number | null;
}

const monthKeys = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const toLocalDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDate = (value: string, delta: number) => {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + delta);
  return toLocalDateString(date);
};

export default function MartyrologyList(props: any) {
  const langId = localStorage.getItem('lang_id') || '1';
  const [status, setStatus] = useState<MartyrologyStatus | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => toLocalDateString(new Date()));
  const [list, setList] = useState<MartyrologyEntry[]>([]);

  useEffect(() => {
    props.setLoading(true);

    axios.get(ProjectConfig.api_url, {
      params: { request: 'martyrology_status' },
      timeout: 15000,
    })
      .then((response) => {
        setStatus(response.data);
      })
      .catch(() => {
        setStatus({
          available: false,
          browse_ready: false,
          complete: false,
          total_entries: 0,
        });
      })
      .finally(() => {
        props.setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!status?.available || !status.browse_ready) {
      setList([]);
      return;
    }

    props.setLoading(true);

    axios.get(ProjectConfig.api_url, {
      params: {
        request: 'martyrology_list',
        lang_id: langId,
        date: selectedDate,
        page: 1,
        per_page: 250,
      },
      timeout: 15000,
    })
      .then((response) => {
        setList(response.data.list || []);
      })
      .catch(() => {
        setList([]);
      })
      .finally(() => {
        props.setLoading(false);
      });
  }, [
    selectedDate,
    langId,
    status?.available,
    status?.browse_ready,
  ]);

  const dateTitle = useMemo(() => {
    const [year, month, day] = selectedDate.split('-').map(Number);
    const monthLabel = getCalendarioLiturgicoLabel(monthKeys[month - 1] || '').toUpperCase();
    const de = getCalendarioLiturgicoLabel('de').toUpperCase();

    return `${monthLabel} ${day} ${de} ${year}`;
  }, [selectedDate, langId]);

  const navigate = (direction: 'back' | 'today' | 'forward') => {
    if (direction === 'today') {
      setSelectedDate(toLocalDateString(new Date()));
      return;
    }

    setSelectedDate((current) => shiftDate(
      current,
      direction === 'back' ? -1 : 1
    ));
  };

  const renderMeta = (entry: MartyrologyEntry) => {
    const parts = [
      getMartyrologyDesignationLabel(entry.designation),
      entry.place,
      entry.period,
    ].filter(Boolean);

    return parts.join(' · ');
  };

  return (
    <div id="Martyrology" className="module_container">
      <div className="module_topBar">
        <TopBar title={getSantoralLabel('martirologio_romano')} />
      </div>

      <div className="module_content">
        {status && (!status.available || !status.browse_ready) && (
          <div className="CardContents">
            <p>{getSantoralLabel('martirologio_no_disponible')}</p>
          </div>
        )}

        {status?.available && status.browse_ready && (
          <>
            <div className="carousel-title martyrology-date-title">
              <h1>
                <strong>{dateTitle}</strong>
              </h1>
            </div>

            <div id="task_list_container" className="flex_column martyrology-list">
              {list.map((entry) => (
                <Link
                  to={`/santoral/martirologio/${encodeURIComponent(entry.id)}`}
                  className="CardList martyrology-card"
                  key={entry.id}
                  onClick={() => props.setLoading(true)}
                >
                  <div className="CardListRight">
                    <span className="CardListTitle">{entry.name}</span>
                    {renderMeta(entry) && (
                      <span className="CardListSubTitle">
                        {renderMeta(entry)}
                      </span>
                    )}
                  </div>
                </Link>
              ))}

              {list.length === 0 && (
                <div className="martyrology-empty">
                  <p>{getSantoralLabel('sin_resultados')}</p>
                </div>
              )}
            </div>

            <div className="nav-buttons">
              <Button variant="outlined" onClick={() => navigate('back')}>
                <strong>{getSistemaLabel('atras')}</strong>
              </Button>
              <Button variant="outlined" onClick={() => navigate('today')}>
                <strong>{getSistemaLabel('hoy')}</strong>
              </Button>
              <Button variant="outlined" onClick={() => navigate('forward')}>
                <strong>{getSistemaLabel('siguiente')}</strong>
              </Button>
            </div>

          </>
        )}
      </div>
    </div>
  );
}

